<!-- eslint-disable max-len -->
<template>
  <div
    id="login-html"
    class="login-html">
    <div class="login-box">
      <div class="d-flex flex-column align-items-center">
        <b-img
          :src="logo_company"
          height="164"/>
        <p class="login-title m__t--20">Sign in to your account</p>
      </div>
      <div
        v-if="two_factor_activated"
        class="two-factor-activated"
        data-test-id="two-factor-activated-banner"
        role="status">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          aria-hidden="true">
          <circle
            cx="12"
            cy="12"
            r="9"
            fill="none"
            stroke="#3D7FE0"
            stroke-width="2" />
          <path
            d="M12 11 V16.5 M12 7.5 V7.6"
            stroke="#3D7FE0"
            stroke-width="2"
            stroke-linecap="round" />
        </svg>
        <p>
          <strong>Two-Factor Authentication is on</strong>
          All users in your company have been logged out. Sign in again with your password, then enter the code we send to your email.
        </p>
      </div>
      <b-form
        @reset="onReset"
        @keyup.enter="submitSignIn">
        <b-form-group>
          <b-form-input
            id="exampleInput1"
            v-model="$v.form.email.$model"
            :class="{'error-input' : $v.form.email.$error}"
            :error-message="null"
            placeholder="Email Address"
            style="border: 1px solid #D0D0D0;"
            type="email"/>
        </b-form-group>
        <b-form-group id="passwordInput">
          <b-form-input
            id="exampleInput2"
            :type="password_id"
            :error-message="null"
            :class="{'error-input' : $v.form.password.$error}"
            v-model="$v.form.password.$model"
            style="border: 1px solid #D0D0D0;"
            placeholder="Password"/>
          <label>
            <i
              :class="{'fa fa-eye': !show_pass, 'fa fa-eye-slash': show_pass}"
              role="img"
              @click="show_pass=!show_pass"/>
          </label>
        </b-form-group>
        <vue-recaptcha
          v-if="captcha_login"
          ref="recaptcha"
          :load-recaptcha-script="true"
          :sitekey="sitekey"
          class="g-recaptcha"
          @verify="onCaptchaVerified"
          @expired="onCaptchaExpired"/>
        <b-row class="mt-2">
          <b-col class="mt-4">
            <b-button
              id="btn-login"
              :class="{'disabled': $v.form.$invalid || (captcha_login && (!recaptchaToken || !publicKey))}"
              :disabled="$v.form.$invalid"
              @click="submitSignIn">
              Login
            </b-button>
          </b-col>
        </b-row>
      </b-form>
      <div
        v-if="withLDAP"
        class="other-signin"
      >
        <p>Or</p>
        <router-link
          to="/signin-with-ldap"
          class="btn-active-directory">
          Sign in with <span>Active Directory</span>
        </router-link>
      </div>
    </div>
    <div class="login-copy">
      <p>Version {{ version_code }}</p>
      <p>Powered by
        <b-link
          href="https://officeless.studio/"
          target="_blank">
          <strong>Jojonomic</strong>
        </b-link>
      </p>
    </div>

    <j-modal ref="modalOtp">
      <!-- The legacy step stays mounted as before; the code step exists only during a challenge. -->
      <input-otp
        v-show="!login_challenge"
        :otp-show="otp_show"
        :wait-time="data_otp ? data_otp.expired_millisecond : 12909309"
        mode="login"
        @close-modal="hideOtpModal"
      />
      <input-login-challenge
        v-if="login_challenge"
        :otp-show="otp_show"
        @close-modal="hideChallengeModal"
        @session-ended="endChallengeSession"
      />
    </j-modal>
  </div>
</template>

<script>
/* eslint-disable no-console, max-len, no-return-await */
import { required, minLength } from 'vuelidate/lib/validators';
import { mapState, mapActions } from 'vuex';
import VueRecaptcha from 'vue-recaptcha';
import inputOtp from '@/components/inputs/jInputOtp';
import inputLoginChallenge from '@/components/inputs/jInputLoginChallenge';
import { activationBanner } from '@/mixins/jMixTwoFactor';
import jModal from '@/components/modal/jModal';

const NodeRSA = require('node-rsa');

export default {
  name: 'JLogin',
  components: {
    VueRecaptcha,
    inputOtp,
    inputLoginChallenge,
    jModal,
  },
  data() {
    return {
      form: {
        email: '',
        password: '',
      },
      show_pass: false,
      // Set by the company setting page when the Admin turned two-factor authentication on.
      two_factor_activated: activationBanner.shown(),
      version_code: '2.64.4',
      publicKey: '',
      nRSA: new NodeRSA(),
      otp_show: false,
      recaptchaToken: null,
    };
  },
  validations: {
    form: {
      email: {
        required,
      },
      password: {
        required,
        minLength: minLength(5),
      },
    },
  },
  computed: {
    ...mapState('jStoreLogin', ['data_otp', 'login_challenge']),

    password_id() {
      if (this.show_pass) {
        return 'text';
      }
      return 'password';
    },
    logo_company() {
      // eslint-disable-next-line
      return require('@/assets/img/studio-logo-new.png');
    },
    withLDAP() {
      return process.env.SIGNIN_WITH_LDAP === 'true';
    },
    login_otp() {
      return process.env.LOGIN_OTP === 'true';
    },
    captcha_login() {
      return process.env.CAPTCHA_LOGIN === 'true';
    },
    sitekey() {
      return process.env.SITE_KEY_RECAPTCHA;
    },
  },
  watch: {
    data_otp(val) {
      if (val) this.showOtpModal();
    },
    // Only a new challenge opens the step: a resend updates the same object, and the
    // legacy step may already be open.
    login_challenge(val, old) {
      if (val && !old && !this.otp_show) this.showOtpModal();
    },
  },
  created() {
    this.getKeyPublicRSA().then((res) => {
      if (res && !res.error) {
        this.publicKey = atob(res.data.key);
        this.nRSA.importKey(this.publicKey, 'pkcs8-public-pem');
        this.nRSA.setOptions({ encryptionScheme: 'pkcs1' });
      }
    });
  },
  methods: {
    ...mapActions('jStoreLogin', ['loginAccount', 'loginAccountV2', 'getKeyPublicRSA', 'requestOtpLogin']),

    async encryption(str) {
      return await this.nRSA.encrypt(str, 'base64');
    },
    // The activation banner hides on the first sign-in attempt.
    submitSignIn() {
      this.two_factor_activated = false;
      activationBanner.set(false);
      this.onSubmit();
    },
    onSubmit() {
      if (this.captcha_login && this.recaptchaToken && this.publicKey) {
        this.encryption(this.form.password).then((result) => {
          this.$v.form.$touch();
          if (!this.form.email.match(/^[A-Za-z0-9@._-]+$/g)) {
            this.$store.dispatch('jStoreNotificationScreen/toggleProblem', 'Please input email correctly');
          } else if (!this.$v.form.$error) {
            const data = {
              email: this.form.email,
              password: result,
              token_recaptcha: this.recaptchaToken,
              app: 'studio',
            };
            if (this.login_otp) this.requestOtpLogin(data);
            else this.loginAccountV2(data);
          }
        }).catch((err) => { console.log(err); });
      } else if (!this.captcha_login) {
        this.encryption(this.form.password).then((result) => {
          this.$v.form.$touch();
          if (!this.form.email.match(/^[A-Za-z0-9@._-]+$/g)) {
            this.$store.dispatch('jStoreNotificationScreen/toggleProblem', 'Please input email correctly');
          } else if (!this.$v.form.$error) {
            const data = {
              email: this.form.email,
              password: result,
              app: 'studio',
            };
            if (this.login_otp) this.requestOtpLogin(data);
            else this.loginAccountV2(data);
          }
        }).catch((err) => { console.log(err); });
      }
    },
    onReset() {
      this.form.email = '';
      this.form.password = '';
      this.recaptchaToken = null;
    },
    showOtpModal() {
      this.$refs.modalOtp.toggleModal();
      this.otp_show = true;
    },
    hideOtpModal() {
      this.$refs.modalOtp.toggleModal();
      this.otp_show = false;
    },
    hideChallengeModal() {
      // toggleModal flips visibility, so a second hide would reopen the modal.
      if (!this.otp_show) return;
      this.hideOtpModal();
      // The captcha token is single use, so a new sign-in needs a fresh one.
      if (this.captcha_login && this.$refs.recaptcha) this.onCaptchaExpired();
    },
    endChallengeSession() {
      this.hideChallengeModal();
      this.onReset();
    },
    onCaptchaExpired() {
      this.$refs.recaptcha.reset();
      this.recaptchaToken = null;
    },
    onCaptchaVerified(recaptchaToken) {
      this.recaptchaToken = recaptchaToken;
    },
  },
};
</script>
<style scoped>
.two-factor-activated{
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin: 16px 0;
  padding: 12px 14px;
  background: #EAF2FD;
  border: 1px solid #BCD5F7;
  border-radius: 8px;
}
.two-factor-activated svg{
  flex: none;
  margin-top: 2px;
}
.two-factor-activated p{
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #1F3F73;
}
.two-factor-activated strong{
  display: block;
  font-family: 'Poppins-Bold';
  font-weight: normal;
}
#login-html.login-html{
  background-image: url('../../assets/img/bg-login.png') !important;
  background-size: contain cover;
  background-position: center center;
}

#login-html.login-html .login-box .login-title {
  font-size: 18px;
  text-align: center;
  color: '#4F4F4F';
}

#login-html.login-html .login-box .login-separator {
  font-size: 12px;
  text-align: center;
  color: #fff;
}

#login-html.login-html .login-box .btn {
  padding: 12px 0;
}

#login-html.login-html .login-box .b-form-group input[type='password'],
#login-html.login-html .login-box .b-form-group input[type='text'] {
  margin-top:15px;
}

#login-html.login-html .login-box .b-form-group input[type='password'],
#login-html.login-html .login-box .b-form-group input[type='text'] {
  padding-top:20px;
  padding-bottom:20px;
  background-image: url('../../assets/img/ico-lock.png') !important;
}

#login-html.login-html .login-box .b-form-group input[type='email'] {
  padding-top:20px;
  padding-bottom:20px;
  background-image: url('../../assets/img/ico-mail.png') !important;
}

#login-html.login-html .login-copy {
  position: absolute;
  color: white;
  bottom: 10px;
  text-align: center;
  width: 100%;
}

#login-html.login-html .login-copy p{
  margin: 10px 0;
  color: #F4F4F4;
  font-size: 11px;
}

#login-html.login-html .login-copy p strong{
  font-weight: bold;
  color: white;
}

#passwordInput, #passwordInput > div {
  position: relative;
}

#passwordInput > div label{
  position: absolute;
  top: 24px;
  right: 11px;
  font-size: 26px;
}

.outline-btn {
  border: solid 1.6px #dfdfdf;
  border-radius: 7px;
  background-color: rgba(121, 121, 121, 0.29);
}

.g-recaptcha {
  transform: scale(1.14);
  transform-origin: 0px 0px;
  margin-top: 35px;
}
</style>



// WEBPACK FOOTER //
// src/views/login/jLogin.vue